import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Stack } from "@mui/material";
import { ProductType } from "@repo/api";
import { AMLFormDefaultValues, type AMLFormValues, type PensionContractType } from "@repo/logic";
import { AMLQuestionsForm, AMLQuestionsSchema, mapAMLRequest } from "@repo/ui";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { usePrimaryAPIClient } from "../../../context/PrimaryAPIClient";
import { mapFetchApiErrorToSubmitStatus } from "../../Drawer/GlobalSteps/SubmitStatusStep";
import { ModalFooter } from "../../Drawer/ModalFooter";
import { type MultiStepChildren } from "../../Drawer/MultiStepDrawer";

type IGetAMLQuestionDrawerStep = {
    contractId: string;
    contractType: PensionContractType;
    onSubmitCallback?: (error?: Error) => void;
    helpText: string;
    saveText: string;
};

export const GetAMLQuestionsDrawerStep = ({
    contractId,
    contractType,
    onSubmitCallback,
    helpText,
    saveText,
}: IGetAMLQuestionDrawerStep): MultiStepChildren => {
    const { contractsDpsApi, contractsPpApi } = usePrimaryAPIClient();
    return {
        component: (
            <AMLQuestionsDrawerStepContent
                contractId={contractId}
                contractType={contractType}
                onSubmitCallback={onSubmitCallback}
                helpText={helpText}
                saveText={saveText}
                contractsDpsApi={contractsDpsApi}
                contractsPpApi={contractsPpApi}
            />
        ),
        stepName: "AMLQuestions",
        stepConfig: {
            disableFooter: true,
            forceFullView: true,
        },
    };
};

type AMLQuestionsDrawerStepContentProps = IGetAMLQuestionDrawerStep & {
    contractsDpsApi: ReturnType<typeof usePrimaryAPIClient>["contractsDpsApi"];
    contractsPpApi: ReturnType<typeof usePrimaryAPIClient>["contractsPpApi"];
};

const AMLQuestionsDrawerStepContent = ({
    contractId,
    contractType,
    onSubmitCallback,
    helpText,
    saveText,
    contractsDpsApi,
    contractsPpApi,
}: AMLQuestionsDrawerStepContentProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const methods = useForm<AMLFormValues>({
        defaultValues: AMLFormDefaultValues,
        resolver: yupResolver(AMLQuestionsSchema) as any,
        shouldFocusError: false,
    });
    const formRef = useRef(methods);
    formRef.current = methods;

    const handleFormSubmit = async () => {
        const { handleSubmit } = formRef.current;
        await handleSubmit(async (values) => {
            try {
                setIsSubmitting(true);
                if (contractType === ProductType.Uf) {
                    await contractsDpsApi.setDPSAmlIdentificationPut({
                        contractId,
                        setAmlIdentificationRequest: mapAMLRequest(values),
                    });
                } else {
                    await contractsPpApi.setPPAmlIdentificationPut({
                        contractId,
                        setAmlIdentificationRequest: mapAMLRequest(values),
                    });
                }
                setIsSubmitting(false);
                onSubmitCallback?.();
            } catch (error) {
                setIsSubmitting(false);
                mapFetchApiErrorToSubmitStatus(error);
                onSubmitCallback?.();
            }
        })();
    };

    return (
        <Stack
            sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                justifyContent: "space-between",
            }}
        >
            <Box sx={{ overflowY: "auto", py: 7, px: 4 }}>
                <FormProvider {...methods}>
                    <AMLQuestionsForm helpText={helpText} autoScrollFirstError={false} />
                </FormProvider>
            </Box>
            <ModalFooter>
                <LoadingButton
                    variant="contained"
                    disableElevation
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() => void handleFormSubmit()}
                >
                    {saveText}
                </LoadingButton>
            </ModalFooter>
        </Stack>
    );
};
